import { createSelector } from "reselect"
import intersection from 'lodash/intersection'
import {
  fromCityRoutesSelector,
  toCityRoutesSelector,
  fromCityTypeSelector,
  toCityTypeSelector
} from './CitySelectors'
import { dateSelector } from './DateSelectors'
import { isAlimanDispatcherSelector, isModultransPartnerSelector } from './UserSelectors'
import { busesSelector, carriersSelector } from './GlobalSelectors'

import { getCarriageNum } from 'services/helpers'
import { DEFAULT_TOTAL_LIMIT, ALIMAN_ROUTES, MODULTRANS_ROUTES } from 'constants/config';

const getState = state => state.route
const isLoadingSelector = state => getState(state).isLoading
const routeSelector = state => getState(state).route
const infoSelector = state => getState(state).info
const waitingPassengers = state => getState(state).waitingPassengers
const isWaitingPassengersLoading = state => getState(state).isWaitingPassengersLoading

const routesSelector = createSelector(
  getState,
  isAlimanDispatcherSelector,
  isModultransPartnerSelector,
  ({ routes }, isAlimanDispatcher, isModultransPartner) => {
    if (isModultransPartner) {
      return routes.filter(({ id }) => MODULTRANS_ROUTES.includes(id))
    }

    if (isAlimanDispatcher) {
      return routes.filter(({ id }) => ALIMAN_ROUTES.includes(id))
    }

    return routes
  }
)

const isWaitingPassengersLoadingSelector = createSelector(
  isWaitingPassengersLoading,
  value => value
)

const waitingPassengersSelector = createSelector(
  waitingPassengers,
  value => value
)

const waitingPassengersQtySelector = createSelector(
  waitingPassengers,
  value => value.length
)

const waitingPassengersIsEmptySelector = createSelector(
  waitingPassengersSelector,
  isLoadingSelector,
  (waitingPassengers, isLoading) => !isLoading && !waitingPassengers.length
)

const disabledCitiesSelector = createSelector(
  infoSelector,
  ({ disabledCities }) => disabledCities || []
)

const bookedPlacesSelector = createSelector(
  infoSelector,
  ({ bookedPlaces }) => bookedPlaces || []
)

const carrierSelector = createSelector(
  infoSelector,
  ({ carrier }) => carrier || null
)

const carrierLabelSelector = createSelector(
  carriersSelector,
  carrierSelector,
  (carriers, carrier) => {
    const c = carriers.find(({ id }) => id === carrier)

    return c ? c.label : ''
  }
)

const busSelector = createSelector(
  infoSelector,
  ({ busNumber }) => busNumber || ''
)

const busQtySelector = createSelector(
  busesSelector,
  busSelector,
  (buses, bus) => {
    const b = buses.find(({ label }) => label === bus.trim())

    return b ? b.qty : 59
  }
)

const placesSelector = createSelector(
  busQtySelector,
  busQty => {
    const array = []

    for (let i = 1; i <= busQty; i++) {
      array.push(i)
    }

    return array
  }
)

const routeIdSelector = createSelector(
  routeSelector,
  route => route ? route.id : null
)

const routeNameSelector = createSelector(
  routeSelector,
  route => route ? route.name : null
)

const routeTypeSelector = createSelector(
  routeSelector,
  route => route ? route.type : null
)

const fromUaRouteSelector = createSelector(
  routeTypeSelector,
  type => type === 'ua'
)

const routeCompanySelector = createSelector(
  routeSelector,
  route => route ? route.company : null
)

const routesForCitiesSelector = createSelector(
  fromCityRoutesSelector,
  toCityRoutesSelector,
  (fromCityRoutes, toCityRoutes) => intersection(fromCityRoutes, toCityRoutes)
)

const isDisabledSelector = createSelector(
  infoSelector,
  routeIdSelector,
  dateSelector,
  ({ disabled }, routeId, date) => {
    if (disabled !== undefined) {
      return disabled
    }

    /* eslint-disable */
    switch (routeId) {
      case "salerno_zaporizhia":
        return date.getDay() !== 0
      case "zaporizhia_salerno":
        return date.getDay() !== 4
      case "cherkasy_plzen":
        return date.getDay() !== 5 && date.getDay() !== 0
      case "plzen_cherkasy":
        return date.getDay() !== 2 && date.getDay() !== 0
      case "kyiv_prague":
        return date.getDay() !== 4
      case "prague_kyiv":
        return date.getDay() !== 6
      default:
        return disabled || false
    }
    /* eslint-enable */
  }
)

const directionSelector = createSelector(
  fromCityTypeSelector,
  toCityTypeSelector,
  (fromCityType, toCityType) => {
    if (fromCityType === 'ua' && toCityType === 'pl') {
      return 'ua'
    } else if (fromCityType === 'ua' && toCityType === 'cz') {
      return 'ua'
    } else if (fromCityType === 'pl' && toCityType === 'cz') {
      return 'ua'
    } else if (fromCityType === 'pl' && toCityType === 'ua') {
      return 'pl'
    } else if (fromCityType === 'cz' && toCityType === 'ua') {
      return 'cz'
    } else if (fromCityType === 'cz' && toCityType === 'pl') {
      return 'cz'
    } else if (fromCityType === 'ua' && toCityType === 'it') {
      return 'ua'
    } else if (fromCityType === 'it' && toCityType === 'ua') {
      return 'it'
    }
  }
)

const shownRoutesSelector = createSelector(
  routesSelector,
  routesForCitiesSelector,
  directionSelector,
  (routes, routesForCities, routeDirection) => (
    routes.reduce((routes, { type, id }) => {
      if (routesForCities.includes(id) && type === routeDirection) {
        routes.push(id)
      }

      return routes
    }, [])
  )
)

const totalSheetBookingsSelector = createSelector(
  infoSelector,
  ({ totalSheetBookings }) => totalSheetBookings || null
)

const limitSelector = createSelector(
  infoSelector,
  ({ limit }) => limit === undefined ? DEFAULT_TOTAL_LIMIT : limit
)

const infoForDriversSelector = createSelector(
  infoSelector,
  ({ infoForDrivers }) => infoForDrivers || ''
)

const infoForPartnersSelector = createSelector(
  infoSelector,
  ({ infoForPartners }) => infoForPartners || ''
)

const firstDriverSelector = createSelector(
  infoSelector,
  ({ driver_1 }) => driver_1 || ''
)

const secondDriverSelector = createSelector(
  infoSelector,
  ({ driver_2 }) => driver_2 || ''
)

const busNumberSelector = createSelector(
  infoSelector,
  ({ busNumber }) => busNumber || ''
)

const sourcesDataSelector = createSelector(
  infoSelector,
  ({ sourcesData }) => sourcesData || []
)

const carriagePhonesSelector = createSelector(
  routeIdSelector,
  dateSelector,
  (routeId, date) => getCarriageNum(date, routeId) || []
)

const fromUaRoutesSelector = createSelector(
  routesSelector,
  routes => routes.filter(({ type }) => type === 'ua')
)

const isFromUaRouteSelector = createSelector(
  fromUaRoutesSelector,
  routeIdSelector,
  (routes, routeId) => !!routes.find(({ id }) => id === routeId)
)

export {
  routesSelector,
  isFromUaRouteSelector,
  fromUaRoutesSelector,
  routeSelector,
  routeIdSelector,
  isLoadingSelector,
  routeNameSelector,
  routeTypeSelector,
  routesForCitiesSelector,
  directionSelector,
  shownRoutesSelector,
  totalSheetBookingsSelector,
  limitSelector,
  sourcesDataSelector,
  carriagePhonesSelector,
  carrierSelector,
  fromUaRouteSelector,
  carrierLabelSelector,
  firstDriverSelector,
  secondDriverSelector,
  infoForDriversSelector,
  busNumberSelector,
  busQtySelector,
  isDisabledSelector,
  disabledCitiesSelector,
  infoForPartnersSelector,
  busSelector,
  bookedPlacesSelector,
  routeCompanySelector,
  placesSelector,
  waitingPassengersSelector,
  waitingPassengersIsEmptySelector,
  waitingPassengersQtySelector,
  isWaitingPassengersLoadingSelector
}


// WEBPACK FOOTER //
// ./src/reduxModules/selectors/RouteSelectors.js